// Command registry for the "/" command palette (7 Sep 2026).
//
// The palette (components/CommandPalette.jsx) searches four sources:
//   1. Actions   - anything registered here with registerCommand()/useRegisterCommands().
//                  A page or feature calls the hook once and its actions appear in "/" search
//                  automatically, for as long as the page is mounted (or forever, when
//                  registered at module scope).
//   2. Pages     - the sidebar's navItems (exported from components/layout/Sidebar.js), so a
//                  new menu entry is searchable the moment it is added to the menu. A frozen
//                  fallback copy is used only if the export is missing.
//   3. Settings  - the Settings page's sections (SETTINGS_SECTIONS below, ids = SettingsPage.js
//                  SETTINGS_CATEGORIES[*].sections[*].id). Opens /ats/settings?section=<id>.
//   4. Backend   - GET /api/search/catalogue (routes/global_search.py) lists every mounted
//                  /api route, so a new backend module shows under "Features" with no edit here.
// Live data (candidates, resumes, requirements, clients, jobs) comes from
// GET /api/search/global?q= and is not part of the registry.
//
// Command shape:
//   { id: 'candidates.add',           // stable, unique; re-registering the same id replaces it
//     label: 'Add candidate',
//     section: 'Candidates',          // group heading inside "Actions"
//     to: '/ats/candidates?action=add' // navigate ...
//     run: () => {},                  // ... OR run a function (to wins when both are given)
//     keywords: 'resume cv upload',   // extra words the search matches on
//     minRole: 'recruiter',           // optional: hidden from users below this role
//     agencyOnly: false,              // optional: hidden without agency permission (like Header)
//     hint: 'Q' }                     // optional: shown on the right (e.g. a hotkey)

import { useEffect } from 'react';
import * as SidebarModule from '../components/layout/Sidebar';

const commands = new Map();
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try { fn(); } catch (e) { /* a broken listener must not break the others */ }
  });
}

export function registerCommand(cmd) {
  if (!cmd || !cmd.id || !cmd.label) {
    if (process.env.NODE_ENV !== 'production') console.warn('registerCommand: id and label are required', cmd);
    return () => {};
  }
  const stored = { section: 'Actions', ...cmd };
  commands.set(cmd.id, stored);
  notify();
  return () => {
    // only remove if nobody re-registered the same id in the meantime
    if (commands.get(cmd.id) === stored) { commands.delete(cmd.id); notify(); }
  };
}

export function registerCommands(list) {
  const offs = (list || []).map(registerCommand);
  return () => offs.forEach((off) => off());
}

export function unregisterCommand(id) {
  if (commands.delete(id)) notify();
}

export function getCommands() {
  return Array.from(commands.values());
}

export function subscribeCommands(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook: register a list of commands while the component is mounted.
 *  useRegisterCommands([{ id, label, to|run, keywords }], [deps]) */
export function useRegisterCommands(list, deps = []) {
  useEffect(() => registerCommands(list), deps); // eslint-disable-line react-hooks/exhaustive-deps
}

// ---------------------------------------------------------------------------
// Opening the palette from anywhere (Header pill, hotkeys, other code).
export const PALETTE_EVENT = 'dcs:command-palette';

export function openCommandPalette(query) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PALETTE_EVENT, { detail: { open: true, query: query || '' } }));
}

export function closeCommandPalette() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PALETTE_EVENT, { detail: { open: false } }));
}

// ---------------------------------------------------------------------------
// Default actions. The five below "Create" are the old Header "Quick Action" menu (Q);
// the rest mirror JM's New menu / global search commands.
export const DEFAULT_ACTIONS = [
  { id: 'candidates.add', label: 'Add candidate', section: 'Create', to: '/ats/candidates?action=add', keywords: 'new resume cv upload quick action' },
  { id: 'requirements.add', label: 'New requirement', section: 'Create', to: '/ats/requirements?action=add', keywords: 'add job jd role position quick action' },
  { id: 'submissions.new', label: 'New submission', section: 'Create', to: '/ats/manual-selections', keywords: 'share cv client selection quick action' },
  { id: 'proposal.send', label: 'Send proposal', section: 'Create', to: '/ats/proposal-mailer', keywords: 'email mailer bde quick action', agencyOnly: true },
  { id: 'agreement.generate', label: 'Generate agreement', section: 'Create', to: '/ats/proposal-mailer?tab=agreement', keywords: 'contract sign pdf quick action', agencyOnly: true },
  { id: 'clients.add', label: 'Add client', section: 'Create', to: '/ats/clients?action=add', keywords: 'company new' },
  { id: 'tasks.new', label: 'New task', section: 'Create', to: '/ats/manual-selections/tasks?action=add', keywords: 'todo reminder' },
  { id: 'interviews.schedule', label: 'Schedule interview', section: 'Create', to: '/ats/manual-selections/interviews', keywords: 'round calendar meeting' },
  { id: 'bgv.start', label: 'Start background verification', section: 'Create', to: '/ats/bgv', keywords: 'bgv verify check' },
  { id: 'dossier.generate', label: 'Generate dossier', section: 'Create', to: '/ats/manual-selections/dossiers', keywords: 'profile pdf candidate' },
  { id: 'publiclink.create', label: 'Create public CV link', section: 'Create', to: '/ats/manual-selections/public-links', keywords: 'share link cv' },
  { id: 'nav.help', label: 'Help Center', section: 'Go to', to: '/ats/help', keywords: 'docs guide support ?' },
  { id: 'nav.settings', label: 'Settings', section: 'Go to', to: '/ats/settings', keywords: 'preferences config admin', minRole: 'admin' },
  { id: 'nav.users', label: 'Users', section: 'Go to', to: '/ats/users', keywords: 'staff accounts team', minRole: 'admin' },
  { id: 'nav.dossier-templates', label: 'Dossier templates', section: 'Go to', to: '/ats/dossier-templates', keywords: 'design layout', minRole: 'admin' },
  { id: 'nav.proposal-history', label: 'Proposal history', section: 'Go to', to: '/ats/proposal-mailer/history', keywords: 'sent proposals mailer' },
  { id: 'nav.proposal-deals', label: 'Proposal deals', section: 'Go to', to: '/ats/proposal-mailer/deals', keywords: 'pipeline follow-up' },
];

// Settings page sections - ids must match SettingsPage.js SETTINGS_CATEGORIES.
export const SETTINGS_SECTIONS = [
  { id: 'ai', label: 'AI & LLM', category: 'AI & Automation', keywords: 'provider model gemini anthropic openai key' },
  { id: 'ai_parsing', label: 'AI Parsing', category: 'AI & Automation', keywords: 'cv company parse' },
  { id: 'ai_job_search', label: 'AI Job Search', category: 'AI & Automation', keywords: 'external search engine serpapi' },
  { id: 'engagement', label: 'Engagement', category: 'AI & Automation', keywords: 'unlock spoof inactive alerts' },
  { id: 'scheduled_tasks', label: 'Scheduled Tasks', category: 'AI & Automation', keywords: 'cron background jobs' },
  { id: 'api_usage', label: 'API Usage', category: 'AI & Automation', keywords: 'tokens consumption cost' },
  { id: 'email', label: 'Email', category: 'Communications', keywords: 'smtp provider sender' },
  { id: 'email_templates', label: 'Email Templates', category: 'Communications', keywords: 'content copy' },
  { id: 'notification_center', label: 'Notification Center', category: 'Communications', keywords: 'whatsapp interakt templates events reminder' },
  { id: 'hr_retargeting', label: 'HR Retargeting', category: 'Communications', keywords: 'follow-up automated hr' },
  { id: 'deal_reminders', label: 'Follow-up Reminders', category: 'Communications', keywords: 'proposal deal' },
  { id: 'profile_signature', label: 'Profile & Signature', category: 'Communications', keywords: 'email signature name' },
  { id: 'recruiting_discipline', label: 'Recruiting Discipline', category: 'Communications', keywords: 'sla pipeline rules' },
  { id: 'webhook', label: 'Webhook & ATS', category: 'Integrations', keywords: 'external integration' },
  { id: 'integrations', label: 'Calendar', category: 'Integrations', keywords: 'google calendar' },
  { id: 'verification', label: 'Verification', category: 'Integrations', keywords: 'background bgv' },
  { id: 'system_users', label: 'System Users', category: 'Users & Access', keywords: 'accounts staff' },
  { id: 'permissions', label: 'Permissions', category: 'Users & Access', keywords: 'roles rbac access' },
  { id: 'authentication', label: 'Authentication', category: 'Users & Access', keywords: 'otp login password' },
  { id: 'page_settings', label: 'Page Settings', category: 'Branding & SEO', keywords: 'seo title branding logo' },
  { id: 'page_analytics', label: 'Page Analytics', category: 'Branding & SEO', keywords: 'views visitors' },
  { id: 'jm_import', label: 'Import from JM', category: 'System', keywords: 'migration joiningmail' },
  { id: 'logs', label: 'System Logs', category: 'System', keywords: 'errors notifications' },
  { id: 'system', label: 'Cache & Storage', category: 'System', keywords: 'performance maintenance' },
  { id: 'productivity', label: 'Productivity', category: 'System', keywords: 'idle tracking reports' },
  { id: 'documentation', label: 'Documentation', category: 'System', keywords: 'user guide' },
  { id: 'danger', label: 'Danger Zone', category: 'System', keywords: 'reset delete data' },
];

// Frozen fallback of Sidebar.js navItems (menu-collapse-2026-09-06) - used ONLY when
// Sidebar.js does not export navItems (i.e. the apply script has not been run yet).
const FALLBACK_NAV = [
  { to: '/ats/ceo', label: 'CEO Cockpit', minRole: 'admin', section: 'Today' },
  { to: '/ats', label: 'Dashboard', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/my-desk', label: 'My Desk', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/manual-selections', label: 'Selections Board', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/manual-selections/tasks', label: 'Tasks', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/manual-selections/interviews', label: 'Interviews', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/inbox', label: 'Mailbox', minRole: 'recruiter', section: 'Today' },
  { to: '/ats/clients', label: 'Clients', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/requirements', label: 'Requirements', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/submissions', label: 'Submissions', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/agreements', label: 'Agreements', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/proposal-mailer', label: 'Proposal Mailer', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/client-activity', label: 'Activity', minRole: 'recruiter', section: 'Clients' },
  { to: '/ats/candidates', label: 'Candidates', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/jobs', label: 'Jobs', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/matches', label: 'Matches', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/hr-pipeline', label: 'HR Pipeline', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/manual-selections/dossiers', label: 'Dossiers', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/manual-selections/public-links', label: 'Public CV Links', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/bgv', label: 'Background Verification', minRole: 'recruiter', section: 'Candidates' },
  { to: '/ats/deal-closer', label: 'Deal Closer', minRole: 'recruiter', section: 'Money', agencyOnly: true },
  { to: '/ats/closing', label: 'Closing Desk', minRole: 'recruiter', section: 'Money' },
  { to: '/ats/money-alerts', label: 'Money Alerts', minRole: 'recruiter', section: 'Money', agencyOnly: true },
  { to: '/ats/hired-candidates', label: 'Hired', minRole: 'recruiter', section: 'Money' },
  { to: '/ats/bde', label: 'BDE Engine', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/hr-leads', label: 'HR Leads', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/retargeting', label: 'Retargeting', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/ai-job-search', label: 'AI Job Search', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/master-database', label: 'Master Data', minRole: 'admin', section: 'Growth & Admin' },
  { to: '/ats/jm-import', label: 'JM Import', minRole: 'admin', section: 'Growth & Admin' },
];

/** The sidebar's navItems (live export) or the frozen fallback. */
export function getNavItems() {
  const live = SidebarModule && SidebarModule.navItems;
  if (Array.isArray(live) && live.length) return live;
  return FALLBACK_NAV;
}

export function navItemsAreLive() {
  return Array.isArray(SidebarModule && SidebarModule.navItems);
}

// ---------------------------------------------------------------------------
// Matching helpers shared by the palette (kept here so they are unit-testable
// without React).
export function normalize(s) {
  return (s || '').toString().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Score a static entry against a query. 0 = no match. Higher = better. */
export function scoreEntry(entry, q) {
  const term = normalize(q);
  if (!term) return 1;
  const label = normalize(entry.label);
  if (!label) return 0;
  if (label === term) return 100;
  if (label.startsWith(term)) return 80;
  const words = label.split(' ');
  if (words.some((w) => w.startsWith(term))) return 70;
  if (label.includes(term)) return 60;
  const hay = normalize([entry.keywords, entry.section, entry.category, entry.subtitle, entry.to].filter(Boolean).join(' '));
  if (hay.includes(term)) return 40;
  // every query word appears somewhere (label or keywords), any order
  const parts = term.split(' ').filter(Boolean);
  if (parts.length > 1 && parts.every((p) => label.includes(p) || hay.includes(p))) return 30;
  // initials: "cc" -> "CEO Cockpit"
  if (term.length >= 2 && term.length <= 4 && words.length >= term.length &&
      words.slice(0, term.length).map((w) => w[0]).join('') === term) return 25;
  return 0;
}

export function filterEntries(entries, q, limit) {
  const scored = [];
  entries.forEach((e, i) => {
    const s = scoreEntry(e, q);
    if (s > 0) scored.push({ e, s, i });
  });
  scored.sort((a, b) => b.s - a.s || a.i - b.i);
  const out = scored.map((x) => x.e);
  return typeof limit === 'number' ? out.slice(0, limit) : out;
}

// ---------------------------------------------------------------------------
// Recent items (per browser, localStorage).
export const RECENT_KEY = 'dcs:palette-recent';
export const RECENT_MAX = 8;

export function readRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x) => x && x.id && x.label) : [];
  } catch (e) { return []; }
}

export function pushRecent(item) {
  if (!item || !item.id) return;
  try {
    const cur = readRecent().filter((x) => x.id !== item.id);
    cur.unshift({ id: item.id, label: item.label, subtitle: item.subtitle || null, to: item.to || null, group: item.group || null, at: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)));
  } catch (e) { /* storage unavailable */ }
}

export function clearRecent() {
  try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* ignore */ }
}
